import React from 'react';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator, } from '@/components/ui/breadcrumb';
import { SidebarInset, SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
import { AppSidebar } from '@/routes/console/_authenticated/-features/app-sidebar';
import { createFileRoute, Link, Outlet, useLocation } from '@tanstack/react-router';
import { Separator } from '@/components/ui/separator';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useAuthenticationState } from '@/hooks/use-authentication';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { NotificationDropdown } from '@/routes/console/_authenticated/-features/notification-dropdown';
import { ThemeDialog } from '@/components/dialog/theme-dialog';
import { ChevronLeft } from 'lucide-react';
import { cn } from '@/lib/utils';
import { fetchCampaign } from '@/services/campaign';
import { fetchDriverPayout } from '@/services/driver-payout';
export const Route = createFileRoute('/console/_authenticated/_layout')({
    component: LayoutPage,
});
function LayoutPage() {
    const { pathname } = useLocation();
    const { workspace } = useAuthenticationState();
    const { t } = useTranslation();
    // Resolve the campaign publicId in the URL (when on a campaign sub-route)
    // so the breadcrumb can show the campaign name instead of the opaque id.
    // Pattern is `/console/campaigns/{publicId}/...` — react-query caches the
    // result so subsequent navigations across the same campaign's tabs hit
    // the cache instead of re-fetching.
    const campaignPublicId = React.useMemo(() => {
        const segs = pathname.split('/').filter(Boolean);
        const i = segs.indexOf('campaigns');
        if (i === -1)
            return null;
        const next = segs[i + 1];
        if (!next || next === 'new')
            return null;
        return next;
    }, [pathname]);
    const { data: campaignForBreadcrumb } = useQuery({
        queryKey: ['campaign', campaignPublicId],
        queryFn: () => fetchCampaign(campaignPublicId),
        enabled: campaignPublicId !== null,
        // Keep the cached campaign across rapid tab swaps; the detail route's
        // own loader is the source of truth and will refresh on its own.
        staleTime: 30_000,
    });
    // Same treatment for the payout detail leaf (`/internal/payouts/{publicId}`)
    // so the crumb shows the driver instead of the opaque payout id. Shares the
    // ['driver-payout', id] cache with the detail route.
    const payoutPublicId = React.useMemo(() => {
        const segs = pathname.split('/').filter(Boolean);
        const i = segs.indexOf('payouts');
        if (i === -1)
            return null;
        const next = segs[i + 1];
        return next ?? null;
    }, [pathname]);
    const { data: payoutForBreadcrumb } = useQuery({
        queryKey: ['driver-payout', payoutPublicId],
        queryFn: () => fetchDriverPayout(payoutPublicId),
        enabled: payoutPublicId !== null,
        staleTime: 30_000,
    });
    const renderBreadcrumbs = React.useCallback(() => {
        const segmentsRaw = pathname.split('/').filter(Boolean);
        if (segmentsRaw.length <= 1)
            return null;
        let path = '';
        const segments = segmentsRaw.map((seg) => {
            path += `/${seg}`;
            return {
                key: seg.replace(/-/g, '_'),
                path,
            };
        });
        const last = segments[segments.length - 1];
        const prev = segments[segments.length - 2];
        // Known route segments translated via the common namespace. Anything
        // not in this set is treated as an opaque identifier (publicId etc.)
        // and rendered as-is in monospace so users still see the leaf even if
        // we can't resolve it to a friendly name.
        const KNOWN_SEGMENTS = new Set([
            'console',
            'campaigns',
            'workspace',
            'profile',
            'billing',
            'new',
            'internal',
            'payouts',
            'drivers',
            'assignments',
            'photos',
            'settings',
            'team',
            'feedback',
            'analytics',
            'ab_testing',
            'destinations',
            'smart_links',
            'smart_links_by_destination',
            'customize',
            'security',
            'identity',
            'connections',
            'preferences',
            'onboarding',
        ]);
        // The campaign publicId segment (12-char base62 between `campaigns` and
        // the next sub-route) renders the campaign name instead of the opaque
        // id when we have it cached. Falls back to monospace publicId while
        // the campaign loader is still in flight, so the breadcrumb is never
        // empty.
        const labelFor = (seg) => {
            if (KNOWN_SEGMENTS.has(seg))
                return t(seg);
            if (campaignPublicId !== null && seg === campaignPublicId && campaignForBreadcrumb) {
                return campaignForBreadcrumb.name;
            }
            if (payoutPublicId !== null && seg === payoutPublicId && payoutForBreadcrumb) {
                return payoutForBreadcrumb.driverName;
            }
            return seg;
        };
        const isOpaque = (seg) => {
            if (KNOWN_SEGMENTS.has(seg))
                return false;
            // Campaign id with a resolved name reads as friendly text, not mono.
            if (campaignPublicId !== null && seg === campaignPublicId && campaignForBreadcrumb)
                return false;
            if (payoutPublicId !== null && seg === payoutPublicId && payoutForBreadcrumb)
                return false;
            return true;
        };
        // Backwards-compat alias used in the JSX below.
        const label = labelFor;
        /* ---------------- Mobile ---------------- */
        return (<div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 md:hidden min-w-0">
                    {segments.length > 2 && (<Link to={prev.path} className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-sm text-muted-foreground hover:text-foreground">
                            <ChevronLeft className="h-4 w-4"/>
                            <span className={cn('max-w-30 truncate', isOpaque(prev.key) && 'font-mono text-xs')}>
                                {label(prev.key)}
                            </span>
                        </Link>)}

                    <span className={cn('text-sm font-medium truncate', isOpaque(last.key) && 'font-mono text-xs')}>
                        {label(last.key)}
                    </span>
                </div>

                {/* ---------------- Desktop ---------------- */}
                <Breadcrumb className="hidden md:flex">
                    <BreadcrumbList>
                        {segments.map((seg, index) => {
                const isLast = index === segments.length - 1;
                const opaque = isOpaque(seg.key);
                return (<React.Fragment key={seg.path}>
                                    <BreadcrumbItem>
                                        {isLast ? (<BreadcrumbPage className={cn(opaque && 'font-mono text-xs')}>
                                                {label(seg.key)}
                                            </BreadcrumbPage>) : (<BreadcrumbLink asChild>
                                                <Link to={seg.path} className={cn(opaque && 'font-mono text-xs')}>
                                                    {label(seg.key)}
                                                </Link>
                                            </BreadcrumbLink>)}
                                    </BreadcrumbItem>
                                    {!isLast && <BreadcrumbSeparator />}
                                </React.Fragment>);
            })}
                    </BreadcrumbList>
                </Breadcrumb>
            </div>);
    }, [pathname, t, campaignPublicId, campaignForBreadcrumb, payoutPublicId, payoutForBreadcrumb]);
    const planCode = workspace?.subscription?.plan?.code ?? 'free';
    const planName = workspace?.subscription?.plan?.name ?? 'Free';
    const billingPath = '/console/workspace/billing';
    const planCtaLabel = planCode === 'free' ? t('upgrade') : t('manage_plan');
    return (<SidebarProvider>
            <AppSidebar />

            <div className="h-full w-full max-w-full p-2 space-y-2">
                <SidebarInset className="overflow-x-hidden">
                    <header className="flex h-14 md:h-16 items-center justify-between gap-2 border-b px-3 md:px-4">
                        {/* Left */}
                        <div className="flex min-w-0 items-center gap-2">
                            <SidebarTrigger className="-ml-1"/>
                            <Separator orientation="vertical" className="hidden md:block h-4"/>
                            {renderBreadcrumbs()}
                        </div>

                        {/* Right */}
                        <div className="flex items-center gap-1 md:gap-2">
                            <ThemeDialog />
                            <NotificationDropdown />

                            {/* Desktop plan badge */}
                            <Badge variant="secondary" className="hidden sm:inline-flex capitalize font-semibold md:text-lg">
                                {planName}
                            </Badge>

                            {/* Mobile compact plan badge */}
                            <Badge variant="secondary" className="sm:hidden px-2">
                                {planCode === 'free' ? 'Free' : 'Pro'}
                            </Badge>

                            {/* Desktop CTA */}
                            <Button asChild size="sm" variant={planCode === 'free' ? 'default' : 'outline'} className="hidden md:inline-flex">
                                <Link to={billingPath}>{planCtaLabel}</Link>
                            </Button>
                        </div>
                    </header>

                    <div className="flex flex-1 flex-col gap-4 p-3 md:p-4">
                        <Outlet />
                    </div>
                </SidebarInset>
            </div>
        </SidebarProvider>);
}
